// Single frontend API helper for ALL backend calls (this run + future runs).
//
// - Base URL defaults to same-origin "/api/v1" (the SPA is served by the same
//   Node process, so relative paths work and no CORS is needed). Override with
//   window.__API_BASE__ for dev-against-localhost.
// - Every request sends credentials so the httpOnly auth cookies flow.
// - Responses use the {ok:true,data} / {ok:false,error:{code,message,details?}}
//   envelope. apiGet/apiPost unwrap it: return `data` on ok, otherwise throw an
//   ApiError carrying {code,message,details,status}.
// - A 401 triggers ONE silent refresh (POST /auth/refresh) and a retry of the
//   original request; only if the refresh itself fails is the 401 surfaced as an
//   ApiError so the app can drop to the login screen. The access JWT lives 15
//   minutes and the refresh cookie 30 days — without this the app looked signed
//   in while every request failed, and a reload logged the user out despite a
//   perfectly valid refresh cookie.
// - The refresh is SINGLE-FLIGHT: concurrent 401s share ONE in-flight refresh
//   promise. Refresh ROTATES the token server-side, so N parallel refreshes would
//   race and revoke each other — the dashboard alone fires several requests at
//   once, so this is the normal case, not an edge case.

export const API_BASE =
  (typeof window !== "undefined" && window.__API_BASE__) || "/api/v1";

export class ApiError extends Error {
  constructor(message, { code, details, status } = {}) {
    super(message || "Request failed");
    this.name = "ApiError";
    this.code = code || "error";
    this.details = details;
    this.status = status;
  }
  get isUnauthenticated() {
    return this.status === 401;
  }
}

// The /auth paths that must NEVER trigger an auto-refresh: refreshing them is
// either circular (/refresh) or pointless (nobody is signed in yet). /auth/me is
// exempt too — the app shell handles its own on-load refresh explicitly, and
// leaving it in would fire a doomed /refresh on every anonymous page load.
const NO_REFRESH = [
  "/auth/login",
  "/auth/refresh",
  "/auth/logout",
  "/auth/me",
  "/auth/register",
  "/auth/forgot-password",
  "/auth/reset-password",
];
const skipRefresh = (path) => NO_REFRESH.some((p) => path === p || path.startsWith(p + "?"));

// The ONE in-flight refresh. Every concurrent 401 awaits this same promise, so
// the rotating token is presented exactly once no matter how many requests fail
// together. Cleared on settle, so a later expiry starts a fresh one.
let refreshInFlight = null;

// Resolves true if the session was renewed, false if we are genuinely signed out.
// Never rejects — callers branch on the boolean.
export function refreshSession() {
  if (!refreshInFlight) {
    refreshInFlight = fetch(API_BASE + "/auth/refresh", {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

function sendOnce(method, url, body) {
  return fetch(url, {
    method,
    credentials: "include", // send/receive httpOnly cookies
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

async function request(method, path, body) {
  const url = API_BASE + path;
  let res;
  try {
    res = await sendOnce(method, url, body);
    // Access token expired (or missing) → renew once and replay. All parallel
    // 401s funnel through the same refreshSession() promise.
    if (res.status === 401 && !skipRefresh(path)) {
      const renewed = await refreshSession();
      if (renewed) res = await sendOnce(method, url, body);
    }
  } catch (networkErr) {
    // Network / CORS / server-down failure — no envelope to parse.
    throw new ApiError("Network error — could not reach the server.", {
      code: "network_error",
      status: 0,
    });
  }

  let envelope = null;
  try {
    envelope = await res.json();
  } catch (e) {
    envelope = null;
  }

  if (envelope && envelope.ok === true) {
    return envelope.data;
  }

  // ok:false (with envelope) OR a non-JSON / unexpected error response.
  const err = envelope && envelope.error ? envelope.error : {};
  const code = err.code || (res.status === 401 ? "unauthenticated" : "error");
  const message =
    err.message ||
    (res.status === 401
      ? "You are not signed in."
      : `Request failed (HTTP ${res.status}).`);
  throw new ApiError(message, {
    code,
    details: err.details,
    status: res.status,
  });
}

export function apiGet(path) {
  return request("GET", path);
}

export function apiPost(path, body) {
  return request("POST", path, body);
}

export function apiPatch(path, body) {
  return request("PATCH", path, body);
}

export function apiPut(path, body) {
  return request("PUT", path, body);
}

export function apiDelete(path) {
  return request("DELETE", path);
}

// --- query-string helper (skips undefined/null/empty/false optionals) ---
function qs(params) {
  const parts = [];
  for (const [k, v] of Object.entries(params || {})) {
    if (v === undefined || v === null || v === "" || v === false) continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

// ============================================================================
// Catalog API (W2 — Items, BOMs, Routings). Thin wrappers over the verbs above;
// every catalog screen + future module reuses these instead of building paths.
// Item identity note: the backend has BOTH a uuid `id` and a human `code`
// (e.g. "FG-PKM"). Items are addressed by uuid; BOMs/Routings by itemCode.
// ============================================================================
export const catalog = {
  // Items
  listItems: (opts = {}) =>
    apiGet(
      "/items" +
        qs({
          type: opts.type,
          q: opts.q,
          includeInactive: opts.includeInactive,
          limit: opts.limit,
          offset: opts.offset,
        }),
    ),
  getItem: (id) => apiGet(`/items/${encodeURIComponent(id)}`),
  // PRODUCTION #4: distinct product-group names for the item-form group combobox.
  listGroups: () => apiGet("/items/groups"),
  createItem: (body) => apiPost("/items", body),
  updateItem: (id, body) => apiPatch(`/items/${encodeURIComponent(id)}`, body),
  deactivateItem: (id) => apiPost(`/items/${encodeURIComponent(id)}/deactivate`),
  activateItem: (id) => apiPost(`/items/${encodeURIComponent(id)}/activate`),

  // BOMs (keyed by itemCode)
  listBoms: (opts = {}) =>
    apiGet("/boms" + qs({ q: opts.q, includeInactive: opts.includeInactive })),
  getBom: (itemCode) => apiGet(`/boms/${encodeURIComponent(itemCode)}`),
  createBom: (body) => apiPost("/boms", body),
  updateBom: (itemCode, body) => apiPut(`/boms/${encodeURIComponent(itemCode)}`, body),
  deactivateBom: (itemCode) =>
    apiPost(`/boms/${encodeURIComponent(itemCode)}/deactivate`),
  activateBom: (itemCode) => apiPost(`/boms/${encodeURIComponent(itemCode)}/activate`),
  explodeBom: (itemCode, qty) =>
    apiGet(`/boms/${encodeURIComponent(itemCode)}/explode` + qs({ qty })),

  // Routings (keyed by itemCode)
  listRoutings: (opts = {}) =>
    apiGet("/routings" + qs({ includeInactive: opts.includeInactive })),
  // Production Master: ALL active routable items (FG+SFG) + routing state.
  listRoutableItems: () => apiGet("/routings/items"),
  getRouting: (itemCode) => apiGet(`/routings/${encodeURIComponent(itemCode)}`),
  createRouting: (body) => apiPost("/routings", body),
  updateRouting: (itemCode, body) =>
    apiPut(`/routings/${encodeURIComponent(itemCode)}`, body),
  deactivateRouting: (itemCode) =>
    apiPost(`/routings/${encodeURIComponent(itemCode)}/deactivate`),
  activateRouting: (itemCode) =>
    apiPost(`/routings/${encodeURIComponent(itemCode)}/activate`),
};

// ============================================================================
// Parties API (W3 — Customers). Parties are addressed by their human `code`
// (e.g. "P1"); the backend resolves codes to uuids. Reads/writes are gated by
// requireModule("customers"); the owner session ("*") sees everything.
//   list   → { parties:[{code,name,kind,gstin,parentCode,isActive}], total }
//   tree   → { tree:[{...party, children:[...]}] }  (group → sister nesting)
//   get    → { party:{...,parentCode,childrenCodes} }
//   create/update/activate/deactivate → { party, warnings }
// ============================================================================
export const parties = {
  listParties: (opts = {}) =>
    apiGet(
      "/parties" +
        qs({ kind: opts.kind, q: opts.q, includeInactive: opts.includeInactive }),
    ),
  getPartyTree: () => apiGet("/parties/tree"),
  getParty: (code) => apiGet(`/parties/${encodeURIComponent(code)}`),
  createParty: (body) => apiPost("/parties", body),
  updateParty: (code, body) => apiPatch(`/parties/${encodeURIComponent(code)}`, body),
  deactivateParty: (code) => apiPost(`/parties/${encodeURIComponent(code)}/deactivate`),
  activateParty: (code) => apiPost(`/parties/${encodeURIComponent(code)}/activate`),
};

// ============================================================================
// Sales Orders API (W3). Bodies use friendly CODES (partyCode, itemCode); the
// service resolves them. Status is ALWAYS derived server-side — never call the
// client-side orderStatus()/lineStatus() for wired screens; trust row.status.
//   list   → { orders:[{code,party,channel,orderDate,lineCount,
//                        totalQtyOrdered,totalValue,status}], total }
//   get    → { order:{...,lines:[{lineCode,item,qty*,rate,requiredBy,status}]} }
//   create/cancel/reopen/update → { order, warnings? }
// reads → requireModule("orders"); writes → requirePermission("createOrder").
// NOTE: the per-line fulfill endpoint is intentionally NOT wrapped here — that
// path is W4. Leave the FULFILL / Stock-decision flow on the mock reducer.
// ============================================================================
export const orders = {
  listOrders: (opts = {}) =>
    apiGet(
      "/orders" +
        qs({
          partyCode: opts.partyCode,
          status: opts.status,
          channel: opts.channel,
          q: opts.q,
          includeInactive: opts.includeInactive,
          from: opts.from,
          to: opts.to,
        }),
    ),
  getOrder: (code) => apiGet(`/orders/${encodeURIComponent(code)}`),
  // My Queue 'Stock decision' group: SO lines still awaiting a stock decision
  // (derived status "Pending"). Gated to the fulfill permission.
  //   → { lines:[{orderCode, party:{code,name}, lineCode, item:{code,name},
  //       qtyOrdered, qtyAllocated, pendingQty}], total }
  pendingDecisions: () => apiGet("/orders/pending-decision"),
  createOrder: (body) => apiPost("/orders", body),
  updateOrder: (code, body) => apiPatch(`/orders/${encodeURIComponent(code)}`, body),
  cancelOrder: (code) => apiPost(`/orders/${encodeURIComponent(code)}/cancel`),
  reopenOrder: (code) => apiPost(`/orders/${encodeURIComponent(code)}/reopen`),
  // System Controller oversight (Phase B): hold/close = monitor; delete = owner.
  holdOrder: (code) => apiPost(`/orders/${encodeURIComponent(code)}/hold`),
  unholdOrder: (code) => apiPost(`/orders/${encodeURIComponent(code)}/unhold`),
  closeOrder: (code) => apiPost(`/orders/${encodeURIComponent(code)}/close`),
  deleteOrder: (code) => apiDelete(`/orders/${encodeURIComponent(code)}`),
  // Manual allocation from free stock (W4a). qty optional → backend allocates
  // min(available, qtyOrdered-qtyAllocated). available→reserved; line → Ready/
  // Partially. The per-line "Send to production" path stays mock until W4b.
  fulfillLine: (orderCode, lineCode, qty) =>
    apiPost(
      `/orders/${encodeURIComponent(orderCode)}/lines/${encodeURIComponent(lineCode)}/fulfill`,
      qty == null ? {} : { qty },
    ),
};

// ============================================================================
// Stock / Inventory API (W4a). Items addressed by human `code`. Reads + room
// transfers are gated by requireModule("inventory"); owner "*" passes.
//   listStock  → { stock:[{item:{code,name,type}, available,reserved,wip,
//                          inTransit,buffer, belowBuffer }], total }
//   getStock   → { item, ...buckets, belowBuffer, rooms:[{room,qty}] }
//   getRooms   → { rooms:[{room, items:[{itemCode,name,qty}]}] }
//   transfer/return → the getStock shape for the moved item.
// ============================================================================
export const stock = {
  listStock: (opts = {}) =>
    apiGet(
      "/stock" +
        qs({
          type: opts.type,
          q: opts.q,
          belowBuffer: opts.belowBuffer,
          limit: opts.limit,
          offset: opts.offset,
        }),
    ),
  getStock: (itemCode) => apiGet(`/stock/${encodeURIComponent(itemCode)}`),
  getRooms: () => apiGet("/stock/rooms"),
  transferToRoom: (body) => apiPost("/stock/transfer-to-room", body),
  // Bulk send (WO "Send all to room"): { room, lines:[{itemCode, qty}] } → one atomic call.
  //   → { room, results:[{itemCode, requested, sent, shortfall}], count }
  transferToRoomBatch: (body) => apiPost("/stock/transfer-to-room-batch", body),
  returnFromRoom: (body) => apiPost("/stock/return-from-room", body),
  // Weekly stock-take: SET a room item to a counted absolute value + variance
  // record. room-adjust (single), room-adjust-batch (whole count sheet), and the
  // adjustments history (variance / wastage report).
  //   roomAdjust      → { room, itemCode, systemQty, countedQty, variance }
  //   roomAdjustBatch → { room, adjusted:[...], count }
  //   listAdjustments → { adjustments:[{room,item,systemQty,countedQty,variance,
  //                        reason,adjustedBy,adjustedAt}], total }
  // Opening stock (Phase A): absolutely SET store available (+ buffer) and mint an
  // OPENING batch per item. → { adjusted:[{itemCode,systemQty,countedQty,variance,batchCode}], count }
  storeAdjust: (body) => apiPost("/stock/store-adjust", body),
  storeAdjustBatch: (body) => apiPost("/stock/store-adjust-batch", body),
  roomAdjust: (body) => apiPost("/stock/room-adjust", body),
  roomAdjustBatch: (body) => apiPost("/stock/room-adjust-batch", body),
  listAdjustments: (opts = {}) => apiGet("/stock/adjustments" + qs({ room: opts.room, from: opts.from, to: opts.to })),
};

// ============================================================================
// Batches + QC API (W4a). Batches addressed by `code`. Reads gated by
// requireModule("inventory"); QC gated by requirePermission("fgqc").
//   listBatches → { batches:[{code, item:{code,name}, itemType, qty, remaining,
//                            status, room, producedAt, poCode }], total }
//   getBatch    → { batch:{ ...detail, consumedFrom:[{batchCode,itemCode,qty}],
//                          consumedBy:[{producedBatchCode,qty}] } }
//   qcBatch     → { batch } (pass:true adds to available + FIFO genealogy +
//                  auto-allocate to source SO line; 409 if not 'QC Pending').
// ============================================================================
export const batches = {
  listBatches: (opts = {}) =>
    apiGet(
      "/batches" +
        qs({
          itemCode: opts.itemCode,
          type: opts.type,
          status: opts.status,
          poCode: opts.poCode,
          q: opts.q,
          limit: opts.limit,
          offset: opts.offset,
        }),
    ),
  getBatch: (code) => apiGet(`/batches/${encodeURIComponent(code)}`),
  qcBatch: (code, pass) => apiPost(`/batches/${encodeURIComponent(code)}/qc`, { pass }),
};

// ============================================================================
// Production API (W4b — Production Orders + Work Orders lifecycle). POs/WOs are
// addressed by their human `code` (e.g. "PO-501", "WO-102"). Reads gated by
// requireModule("production"); writes gated per-transition (stockPO/startProd/
// issue/advanceProd); owner "*" passes all.
//   listPOs  → { orders:[{code,item:{code,name},requestedQty,plannedQty,status,
//                forStock,woCode,stepIdx,currentStep:{stepNo,workCenter}|null,
//                issuedQty,sourceLineCodes[],isActive}], total }
//   getPO    → { order:{ ...header, item:{code,name,type}, steps:[{stepNo,
//                workCenter,setupTime,perUnitTime,startedAt,endedAt}],
//                sourceLines:[{lineCode,orderCode}], linkedBatch|null,
//                bomRequirement:{leaves[],intermediates[]}|null } }
//   create/lifecycle → { order } (always the fresh getPO shape)
//   listWorkOrders → { workOrders:[{code,createdAt,poCodes[],poCount}] }
//   getWorkOrder   → { workOrder:{code,createdAt,poCodes[],poCount} }
//   createWorkOrder → { workOrder }
// createPO is dual-mode: make-to-order {sourceLineCodes:[lineCode,...], qty?}
// OR make-to-stock {itemCode, qty, forStock:true}. The item must carry an active
// BOM + active routing, else the backend 400s naming what's missing.
// ============================================================================
export const production = {
  listPOs: (opts = {}) =>
    apiGet(
      "/production/orders" +
        qs({
          status: opts.status,
          itemCode: opts.itemCode,
          forStock: opts.forStock,
          woCode: opts.woCode,
          sourceLineCode: opts.sourceLineCode,
          assignedUserId: opts.assignedUserId,
          completed: opts.completed,
          q: opts.q,
          includeInactive: opts.includeInactive,
          limit: opts.limit,
          offset: opts.offset,
        }),
    ),
  getPO: (code) => apiGet(`/production/orders/${encodeURIComponent(code)}`),
  // My Queue 'Issue materials' group: WO-released POs with RM still to issue.
  // Gated to the 'issue' permission (a stores user without the production module
  // can call it). → { orders:[{code,item:{code,name},plannedQty,issuedQty,status,woCode}], total }
  pendingIssue: () => apiGet("/production/orders/pending-issue"),
  createPO: (body) => apiPost("/production/orders", body),
  // Generating a WO REQUIRES an assignee (server rejects without one).
  generateWO: (code, assigneeId) => apiPost(`/production/orders/${encodeURIComponent(code)}/generate-wo`, { assigneeId }),
  // (Re)assign an existing PO's responsible employee.
  assignPO: (code, assigneeId) => apiPatch(`/production/orders/${encodeURIComponent(code)}/assign`, { assigneeId }),
  startPO: (code) => apiPost(`/production/orders/${encodeURIComponent(code)}/start`),
  issuePO: (code, qty) => apiPost(`/production/orders/${encodeURIComponent(code)}/issue`, { qty }),
  // RM issue batch record (Phase A): record which physical batch(es) were sent.
  //   recordIssuedBatches(code, {itemCode, lines:[{batchCode,qty}], room?})
  //   getIssuedBatches(code) → { poCode, issued:[{item,batchCode,qty,qcDate,room,recordedBy}] }
  recordIssuedBatches: (code, body) => apiPost(`/production/orders/${encodeURIComponent(code)}/issued-batches`, body),
  // VENDOR-WISE (Phase 2, gated): batch-wise issue — { qty, materials:[{itemCode, lines:[{batchCode,qty}]}] }
  // → one atomic tx decrements chosen batch.remaining + aggregate in lockstep.
  issueBatchwise: (code, body) => apiPost(`/production/orders/${encodeURIComponent(code)}/issue-batchwise`, body),
  getIssuedBatches: (code) => apiGet(`/production/orders/${encodeURIComponent(code)}/issued-batches`),
  returnMaterials: (code, qty) =>
    apiPost(`/production/orders/${encodeURIComponent(code)}/return-materials`, { qty }),
  // Production #9: optional actualOutput on finish (advance-last-step / complete). Omit ⇒ planned qty (today).
  advancePO: (code, actualOutput) => apiPost(`/production/orders/${encodeURIComponent(code)}/advance`, actualOutput != null ? { actualOutput } : undefined),
  // Performance Phase 1: manager marks a PO complete (stamps completedAt/by).
  completePO: (code, actualOutput) => apiPost(`/production/orders/${encodeURIComponent(code)}/complete`, actualOutput != null ? { actualOutput } : undefined),
  // LOT-WISE (Phase B, gated by settings.lotWiseProduction): record one date-wise partial lot
  // entry (mints a QC-Pending batch; PO auto-completes when Σ actualOutput ≥ plannedQty).
  recordLotEntry: (code, body) => apiPost(`/production/orders/${encodeURIComponent(code)}/lot-entry`, body),
  cancelPO: (code) => apiPost(`/production/orders/${encodeURIComponent(code)}/cancel`),
  listWorkOrders: () => apiGet("/production/work-orders"),
  getWorkOrder: (code) => apiGet(`/production/work-orders/${encodeURIComponent(code)}`),
  // Consolidated direct RM/PM required to produce ALL the WO's items (shared
  // components summed) + on-hand stock + low-stock flag.
  //   → { woCode, items:[{code,name,plannedQty}],
  //       materials:[{code,name,type,totalRequired,available,buffer,shortBy,status}],
  //       componentCount, shortfallCount }
  getWorkOrderMaterials: (code) =>
    apiGet(`/production/work-orders/${encodeURIComponent(code)}/materials`),
  createWorkOrder: (poCodes) => apiPost("/production/work-orders", { poCodes }),
};

// ============================================================================
// Dispatch / Deliveries API (W5). Delivery challans addressed by `code` (DC-…).
// A "dispatchable" line is allocated-but-unshipped stock ready to load. A single
// DC may span multiple orders/parties; the server enforces the over-dispatch cap
// (qty ≤ dispatchable, else 400 naming the line). Reads gated by
// requireModule("deliveries"); dispatch→requirePermission("dispatch");
// deliver/cancel→requirePermission("deliver").
//   listDispatchable → { dispatchable:[{orderCode,lineCode,party:{code,name},
//                        item:{code,name},qtyOrdered,qtyAllocated,qtyDispatched,
//                        dispatchable}], total }
//   createDispatch   → { delivery }  (new DC, In Transit)
//   listDeliveries   → { deliveries:[{code,via,deliveryDate,status,invoiced,
//                        lineCount,totalQty,parties[],orders[]}], total }
//   getDelivery      → { delivery:{...header, lines:[{orderCode,lineCode,party,
//                        item,qty}], parties[], orders[]} }
//   deliver          → { delivery }   cancelDelivery → { code, cancelled:true }
// ============================================================================
export const dispatch = {
  listDispatchable: (opts = {}) =>
    apiGet(
      "/dispatch/dispatchable" +
        qs({ partyCode: opts.partyCode, orderCode: opts.orderCode, itemCode: opts.itemCode }),
    ),
  createDispatch: (body) => apiPost("/dispatch", body),
  listDeliveries: (opts = {}) =>
    apiGet(
      "/deliveries" +
        qs({
          status: opts.status,
          partyCode: opts.partyCode,
          orderCode: opts.orderCode,
          invoiced: opts.invoiced,
          assignedUserId: opts.assignedUserId,
          delivered: opts.delivered,
          q: opts.q,
          from: opts.from,
          to: opts.to,
        }),
    ),
  getDelivery: (code) => apiGet(`/deliveries/${encodeURIComponent(code)}`),
  deliver: (code) => apiPost(`/deliveries/${encodeURIComponent(code)}/deliver`),
  cancelDelivery: (code) => apiPost(`/deliveries/${encodeURIComponent(code)}/cancel`),
};

// ============================================================================
// Purchases API (W5 — RM/PM procurement + QC). Addressed by `code` (PUR-…).
// Supplier is free-text. QC pass adds qty to stock.available and mints a Passed
// RM/PM batch (purchaseId-linked); reject sets status only. Reads gated by
// requireModule("purchases"); create/cancel→requirePermission("stockPO");
// QC→requirePermission("fgqc").
//   listPurchases → { purchases:[{code,supplier,purchaseDate,status,qcAt,
//                     lineCount,lines:[{itemCode,qty}]}], total }
//   getPurchase   → { purchase:{...,lines:[{item:{code,name,type},qty}],
//                     batches:[{batchCode,itemCode,qty,remaining,status}]} }
//   createPurchase → the purchase detail (status "QC Pending")
//   qcPurchase    → { purchase }   cancelPurchase → { code, cancelled:true }
// ============================================================================
export const purchases = {
  listPurchases: (opts = {}) =>
    apiGet(
      "/purchases" +
        qs({
          status: opts.status,
          supplier: opts.supplier,
          itemCode: opts.itemCode,
          q: opts.q,
          includeInactive: opts.includeInactive,
          from: opts.from,
          to: opts.to,
        }),
    ),
  getPurchase: (code) => apiGet(`/purchases/${encodeURIComponent(code)}`),
  createPurchase: (body) => apiPost("/purchases", body),
  // Classic call: qcPurchase(code, true|false) → { pass }. GRN+partial (flag on):
  // qcPurchase(code, { pass, lines:[{itemCode, receivedQty, passedQty, failedQty?, rejectReason?}] }).
  qcPurchase: (code, passOrBody) => apiPost(`/purchases/${encodeURIComponent(code)}/qc`, typeof passOrBody === "boolean" ? { pass: passOrBody } : passOrBody),
  // GRN INVOICE (Step 8, grnQc): { invoiceNo?, invoiceDate?, invoiceAmount?, fileBase64?, fileName?, mime? }
  //   → { purchase, fileStored, note }. invoiceFile(code) → { url, note } (signed link, fail-soft).
  savePurchaseInvoice: (code, body) => apiPost(`/purchases/${encodeURIComponent(code)}/invoice`, body),
  purchaseInvoiceFile: (code) => apiGet(`/purchases/${encodeURIComponent(code)}/invoice-file`),
  // GRN VERIFICATION (Steps 9/9a): { decision: "approve"|"reject", reason? } → { purchase }.
  verifyPurchaseInvoice: (code, body) => apiPost(`/purchases/${encodeURIComponent(code)}/verify-invoice`, body),
  // ACCOUNTS SIGN-OFF / PO COMPLETE (Step 14): requires a verified invoice → { purchase }.
  completePurchase: (code) => apiPost(`/purchases/${encodeURIComponent(code)}/complete-purchase`),
  // PO PDF (manufacturing): a same-origin, cookie-auth'd URL to open/download the PO document.
  purchasePdfUrl: (code) => `/api/v1/purchases/${encodeURIComponent(code)}/pdf`,
  cancelPurchase: (code) => apiPost(`/purchases/${encodeURIComponent(code)}/cancel`),
  // System Controller oversight (Phase B): hold/close/set-expected = monitor; delete = owner.
  holdPurchase: (code) => apiPost(`/purchases/${encodeURIComponent(code)}/hold`),
  unholdPurchase: (code) => apiPost(`/purchases/${encodeURIComponent(code)}/unhold`),
  closePurchase: (code) => apiPost(`/purchases/${encodeURIComponent(code)}/close`),
  setPurchaseExpected: (code, expectedDate) => apiPatch(`/purchases/${encodeURIComponent(code)}/expected`, { expectedDate }),
  deletePurchase: (code) => apiDelete(`/purchases/${encodeURIComponent(code)}`),
};

// ============================================================================
// Invoices API (W5). Addressed by `code` (INV-…). Generate keys off a delivery:
// a cross-party DC mints ONE invoice PER party (response.invoices is an array).
// Re-generating an already-invoiced DC → 409. Receivables is the debits-only
// customer ledger (Σ invoice totals; no payments modelled). Reads gated by
// requireModule("invoices"); generate→requirePermission("invoice").
//   listInvoices  → { invoices:[{code,deliveryCode,billTo:{code,name},
//                     invoiceDate,lineCount,total}], total }
//   getInvoice    → { invoice:{...,billTo:{code,name,gstin},sourceDelivery,
//                     lines:[{item:{code,name},orderCode,qty,rate,amount}],total} }
//   generateInvoice → { invoices:[ full invoice details … ] }
//   getReceivables() → { receivables:[{party:{code,name},invoiceCount,
//                        totalInvoiced}], note }
//   getReceivables(partyCode) → { party, invoiceCount, totalInvoiced,
//                        invoices:[{code,date,total,runningBalance}] }
// ============================================================================
// ============================================================================
// Payments (Phase 2). Recording money RECEIVED against invoices.
//   create  → { payment:{code,party,amount,allocations:[{invoiceCode,amount,
//               balanceAfter}],allocated,unapplied}, advance }
//   preview → the SAME allocation plan without writing (server runs the real
//             allocator, so the confirmed preview is what gets recorded)
//   partyState → { party, openInvoices:[...], totalOpen, advance }
// Writes need the "invoice" capability (accounts).
// ============================================================================
export const paymentsApi = {
  list: (opts = {}) => apiGet("/payments" + qs({ partyCode: opts.partyCode, from: opts.from, to: opts.to, limit: opts.limit, offset: opts.offset })),
  get: (code) => apiGet(`/payments/${encodeURIComponent(code)}`),
  create: (body) => apiPost("/payments", body),
  preview: (body) => apiPost("/payments/preview", body),
  importPreview: (rows) => apiPost("/payments/import/preview", { rows }),
  importCommit: (rows) => apiPost("/payments/import/commit", { rows }),
  partyState: (code) => apiGet(`/payments/party/${encodeURIComponent(code)}`),
  // The "who's paying?" picker list. Payments-gated (same permission as create),
  // so ACCOUNTS — who lack the customers module — can load it.
  // → { parties:[{code,name,kind,parentCode}], total }  (active only, mother-first)
  pickerParties: () => apiGet("/payments/parties"),
};

export const invoices = {
  listInvoices: (opts = {}) =>
    apiGet(
      "/invoices" +
        qs({
          billToPartyCode: opts.billToPartyCode,
          deliveryCode: opts.deliveryCode,
          orderCode: opts.orderCode,
          q: opts.q,
          from: opts.from,
          to: opts.to,
        }),
    ),
  getInvoice: (code) => apiGet(`/invoices/${encodeURIComponent(code)}`),
  generateInvoice: (deliveryCode) => apiPost("/invoices/generate", { deliveryCode }),
  getReceivables: (partyCode) => apiGet("/receivables" + qs({ partyCode })),
};

// ============================================================================
// People & Org / Settings admin API (Tier 1). Tenant-scoped CRUD, all gated
// requireModule("settings") (owner "*" passes). The mock UI spoke string ids +
// role keys + dept names; the backend resolves friendly keys → uuids on input
// and RETURNS resolved objects for display:
//   users.list  → { users:[{id,name,email, role:{id,key,name},
//                   branch:{id,name}|null, department:{id,name}|null,
//                   reportsTo:{id,name}|null, status, weekOff,inTime,outTime}], total }
//   create/update body → { name,email,password?, roleKey, branchCode?,
//                   departmentName?, reportsToId?, weekOff?,inTime?,outTime? }
//   active↔status: use deactivate/activate (NOT a PATCH field). Guards
//   (last-owner, self-deactivate) surface as ApiError.message.
//   roles.list  → { roles:[{id,key,name,permissions[],navModules[],isSystem,
//                   userCount}] }; PATCH sends the updated arrays.
//   branches.list → { branches:[{id,name,code,type,userCount}] }
//   departments.list → { departments:[{id,name,head:{id,name}|null,userCount}] }
//   company.get → { name,legalName,gstin,pan,cin,addressLine,city,state,
//                   pincode,phone,email,website,currency, slug,plan,status }
//   company PATCH: editable fields ONLY — slug/plan/status are platform-admin
//   owned and ignored server-side.
// ============================================================================
export const users = {
  list: (opts = {}) =>
    apiGet("/users" + qs({ q: opts.q, status: opts.status, roleKey: opts.roleKey })),
  get: (id) => apiGet(`/users/${encodeURIComponent(id)}`),
  create: (body) => apiPost("/users", body),
  update: (id, body) => apiPatch(`/users/${encodeURIComponent(id)}`, body),
  deactivate: (id) => apiPost(`/users/${encodeURIComponent(id)}/deactivate`),
  activate: (id) => apiPost(`/users/${encodeURIComponent(id)}/activate`),
  resetPassword: (id, newPassword) =>
    apiPost(`/users/${encodeURIComponent(id)}/reset-password`, { newPassword }),
  // RETAIL PHASE A: a user's assigned store parties (store-wise users).
  getStores: (id) => apiGet(`/users/${encodeURIComponent(id)}/stores`),
  setStores: (id, storePartyCodes) => apiPut(`/users/${encodeURIComponent(id)}/stores`, { storePartyCodes }),
};

// Work-centre (room) defaults: the default responsible person per room, used to
// PREFILL the assignee when a sales-order line is sent to production.
//   list() → { workCenters:[{ workCenter, defaultUser:{id,name}|null }] }  (rooms IN USE)
//   set({workCenter, defaultUserId})  — defaultUserId null clears the default
export const workCenterDefaultsApi = {
  list: () => apiGet("/work-center-defaults"),
  set: (body) => apiPut("/work-center-defaults", body),
};

// RETAIL Phase 1: warehouses + per-warehouse stock + item→warehouse mapping.
export const warehousesApi = {
  list: (opts = {}) => apiGet("/warehouses" + qs({ includeInactive: opts.includeInactive })),
  create: (body) => apiPost("/warehouses", body),
  update: (code, body) => apiPatch(`/warehouses/${encodeURIComponent(code)}`, body),
  stock: (code) => apiGet(`/warehouses/${encodeURIComponent(code)}/stock`),
  setStock: (code, body) => apiPut(`/warehouses/${encodeURIComponent(code)}/stock`, body),
  // Phase 8A: retail "maintain warehouse stock" setting.
  settings: () => apiGet("/warehouses/settings"),
  setSettings: (body) => apiPut("/warehouses/settings", body),
  // Phase 8B: cross-warehouse pick queue (orders/lines still needing a pick).
  pendingPicks: () => apiGet("/warehouses/pending-picks"),
};
export const itemWarehouseDefaultsApi = {
  list: () => apiGet("/item-warehouse-defaults"),
  set: (body) => apiPut("/item-warehouse-defaults", body),
};

// RETAIL Phase 2: stores (internal parties) + indents + the warehouse view.
export const storesApi = {
  list: () => apiGet("/stores"),
  create: (body) => apiPost("/stores", body),
};
export const indentsApi = {
  list: (opts = {}) => apiGet("/indents" + qs({ storeCode: opts.storeCode })),
  get: (code) => apiGet(`/indents/${encodeURIComponent(code)}`),
  create: (body) => apiPost("/indents", body),
  warehouseOrders: (code) => apiGet(`/warehouses/${encodeURIComponent(code)}/orders`),
  pick: (code, orderCode, body) => apiPost(`/warehouses/${encodeURIComponent(code)}/orders/${encodeURIComponent(orderCode)}/pick`, body),
  unpick: (code, orderCode, body) => apiPost(`/warehouses/${encodeURIComponent(code)}/orders/${encodeURIComponent(orderCode)}/unpick`, body),
  receivable: () => apiGet("/retail-receive"),
  receipt: (code) => apiGet(`/retail-receive/${encodeURIComponent(code)}`),
  receive: (code, body) => apiPost(`/retail-receive/${encodeURIComponent(code)}/receive`, body),
};

// RETAIL P2c: retail Delivery Page (backend shipped 120c673).
export const retailDeliveriesApi = {
  list: (status) => apiGet("/retail-deliveries" + qs({ status })),
  setTarget: (code, target) => apiPut(`/retail-deliveries/${encodeURIComponent(code)}/target`, { target }),
  bulkDeliver: (codes, deliveredByName) => apiPost("/retail-deliveries/bulk-deliver", { codes, deliveredByName }),
};

// RETAIL Phase 6: item packs (multi-UOM). An item's alternate units as factors of
// its base uom. get() → { item, baseUom:{code,name}|null, packs:[{packName,factorToBase,sortOrder,isActive}] }.
// put replaces the whole list; del removes one pack.
export const packsApi = {
  get: (itemCode) => apiGet(`/items/${encodeURIComponent(itemCode)}/packs`),
  put: (itemCode, packs) => apiPut(`/items/${encodeURIComponent(itemCode)}/packs`, { packs }),
  del: (itemCode, packName) => apiDelete(`/items/${encodeURIComponent(itemCode)}/packs/${encodeURIComponent(packName)}`),
};

export const roles = {
  list: () => apiGet("/roles"),
  get: (id) => apiGet(`/roles/${encodeURIComponent(id)}`),
  create: (body) => apiPost("/roles", body),
  update: (id, body) => apiPatch(`/roles/${encodeURIComponent(id)}`, body),
  remove: (id) => apiDelete(`/roles/${encodeURIComponent(id)}`),
};

export const branches = {
  list: () => apiGet("/branches"),
  create: (body) => apiPost("/branches", body),
  update: (id, body) => apiPatch(`/branches/${encodeURIComponent(id)}`, body),
  remove: (id) => apiDelete(`/branches/${encodeURIComponent(id)}`),
};

export const departments = {
  list: () => apiGet("/departments"),
  create: (body) => apiPost("/departments", body),
  update: (id, body) => apiPatch(`/departments/${encodeURIComponent(id)}`, body),
  remove: (id) => apiDelete(`/departments/${encodeURIComponent(id)}`),
};

export const company = {
  get: () => apiGet("/company"),
  update: (body) => apiPatch("/company", body),
  // PUBLIC STORE-ORDER LINK (retail, owner/settings-gated): read current token / generate+rotate.
  getPublicOrderToken: () => apiGet("/company/public-order-token"),
  rotatePublicOrderToken: () => apiPost("/company/public-order-token"),
};

// ============================================================================
// Shifts + Holidays (Tier 2). Gated requireModule("settings") (owner "*").
//   shifts.list → { shifts:[{id,name,inTime,outTime,weekOff:[dow…],
//                   breakMinutes,isActive,userCount}] }
//   create/update body → { name,inTime,outTime, weekOff?:[dow…], breakMinutes? }
//   assign body → { userIds:[uuid…], unassign?:bool } — copies the shift's
//   schedule (in/out/weekOff) onto each user + sets users.shiftId; unassign
//   clears shiftId. → { shift, assigned:[uuid…] }
//   holidays.list → { holidays:[{id,name,date, branch:{id,name}|null
//                   (null=company-wide), isActive}] }; filters year/branchCode.
//   create body → { name, date:"YYYY-MM-DD", branchCode? (omit=company-wide) }.
//   remove = soft-delete (deactivate). Holidays feed the working-day/SLA calc.
// ============================================================================
export const shifts = {
  list: (opts = {}) => apiGet("/shifts" + qs({ includeInactive: opts.includeInactive })),
  get: (id) => apiGet(`/shifts/${encodeURIComponent(id)}`),
  create: (body) => apiPost("/shifts", body),
  update: (id, body) => apiPatch(`/shifts/${encodeURIComponent(id)}`, body),
  deactivate: (id) => apiPost(`/shifts/${encodeURIComponent(id)}/deactivate`),
  activate: (id) => apiPost(`/shifts/${encodeURIComponent(id)}/activate`),
  assign: (id, userIds, unassign = false) =>
    apiPost(`/shifts/${encodeURIComponent(id)}/assign`, { userIds, unassign }),
};

export const holidays = {
  list: (opts = {}) =>
    apiGet("/holidays" + qs({ year: opts.year, branchCode: opts.branchCode, includeInactive: opts.includeInactive })),
  create: (body) => apiPost("/holidays", body),
  update: (id, body) => apiPatch(`/holidays/${encodeURIComponent(id)}`, body),
  remove: (id) => apiDelete(`/holidays/${encodeURIComponent(id)}`),
};

// ============================================================================
// Number-series config (Tier 2). Gated requireModule("settings") (owner "*").
//   list → { series:[{docType,prefix,format,padding,nextNumber,fyBased,
//            currentFy, isActive, example (dry-rendered next number)}] }
//   update body → { prefix?, format?, padding?, fyBased?, nextNumber? } (tokens
//   {PREFIX}/{FY}/{N}|{NNNN}; backend validates + enforces fyBased⇒{FY}).
// ============================================================================
export const numberSeries = {
  list: () => apiGet("/number-series"),
  update: (docType, body) => apiPatch(`/number-series/${encodeURIComponent(docType)}`, body),
};

// ============================================================================
// GST config (Phase 2 UI). Gated requireModule("settings") (owner "*").
//   units.list  → { units:[{id,code,name,isActive}] }
//   hsn.list    → { hsnCodes:[{id,code,description,defaultGstRate,isActive,itemCount}] }
//   gst.stateCodes → { stateCodes:[{code,name}] } (40 GST state/UT codes)
//   gst.computePreview(body) → the Phase-3 engine result (tax preview tool).
// Company gstStateCode goes through company.update/get; item tax fields
// (uomCode/hsnCode/gstRateOverride/unitPrice) through catalog.createItem/updateItem.
// ============================================================================
export const units = {
  list: (opts = {}) => apiGet("/units" + qs({ includeInactive: opts.includeInactive })),
  create: (body) => apiPost("/units", body),
  update: (id, body) => apiPatch(`/units/${encodeURIComponent(id)}`, body),
  deactivate: (id) => apiPost(`/units/${encodeURIComponent(id)}/deactivate`),
  activate: (id) => apiPost(`/units/${encodeURIComponent(id)}/activate`),
};

export const hsn = {
  list: (opts = {}) => apiGet("/hsn-codes" + qs({ includeInactive: opts.includeInactive })),
  create: (body) => apiPost("/hsn-codes", body),
  update: (id, body) => apiPatch(`/hsn-codes/${encodeURIComponent(id)}`, body),
  deactivate: (id) => apiPost(`/hsn-codes/${encodeURIComponent(id)}/deactivate`),
  activate: (id) => apiPost(`/hsn-codes/${encodeURIComponent(id)}/activate`),
};

export const gst = {
  stateCodes: () => apiGet("/gst/state-codes"),
  computePreview: (body) => apiPost("/gst/compute-preview", body),
};

// ============================================================================
// Performance reports (Phase 2). Management data (gated to the "reports" module).
// Durations are WORKING-time (week-offs + holidays excluded); on-time = completed
// on/before the source order's requiredBy. Range params are YYYY-MM-DD, default
// last 30 days by completedAt.
//   employees      → { from,to,durationUnit,unassignedCompleted, employees:[{user,completedCount,avgWorkingDuration,avgRawDurationHours,onTimeCount,onTimeEligible,onTimeRate,...}] }
//   employeeDetail → { user, summary, orders:[{code,item,plannedQty,createdAt,completedAt,workingDuration,rawDurationHours,dueDate,onTime}] }
//   summary        → company-wide totals
// Phase 3: `type` scopes to production | dispatch | all (default all).
// ============================================================================
// Reports API (Phase A — sales aggregation). One endpoint, three roll-up levels:
//   sales({level:'order'})  → { rows:[{orderCode,orderDate,customer:{code,name},
//     motherName, orderedQty,deliveredQty,pendingQty,fulfilmentPct,status,
//     orderValue,invoicedValue}], total }
//   sales({level:'sister'}) → per-customer rollup (+ motherName, orders count)
//   sales({level:'mother'}) → per top-level group (self + sisters), sisters count
// from/to filter on orderDate. Gated by the "reports" module.
// ============================================================================
export const reports = {
  sales: (opts = {}) => apiGet("/reports/sales" + qs({ level: opts.level, from: opts.from, to: opts.to })),
  // KPI/KRA scorecard: per-employee task aggregate + REUSED performance domain.
  //   → { from,to, rows:[{userId,name, assigned,completed,pending,overdue,taskOnTimeRate,
  //       jobsCompleted,productionCount,dispatchCount,avgWorkingDays,processOnTimeRate,
  //       overallOnTimeRate}], note }
  kpi: (opts = {}) => apiGet("/reports/kpi" + qs({ from: opts.from, to: opts.to })),
  // Transaction ledger: per-customer SO→DC→INV event timeline (+ running balance).
  //   → { partyCode, rows:[{date,type,docCode,party,description,qty,amount,status,balance}], total, totalInvoiced, note }
  statement: (opts = {}) => apiGet("/reports/statement" + qs({ partyCode: opts.partyCode, from: opts.from, to: opts.to })),
  ledger: (opts = {}) => apiGet("/reports/ledger" + qs({ partyCode: opts.partyCode, from: opts.from, to: opts.to })),
  // Customer list for the ledger picker — reports-gated (so reports users without
  // the "customers" module can still pick a customer). → { parties:[{code,name,kind,parentCode}], total }
  ledgerCustomers: () => apiGet("/reports/ledger/customers"),
};

export const performance = {
  employees: (opts = {}) => apiGet("/performance/employees" + qs({ from: opts.from, to: opts.to, type: opts.type })),
  employeeDetail: (userId, opts = {}) =>
    apiGet(`/performance/employees/${encodeURIComponent(userId)}` + qs({ from: opts.from, to: opts.to, type: opts.type })),
  summary: (opts = {}) => apiGet("/performance/summary" + qs({ from: opts.from, to: opts.to, type: opts.type })),
};

// ============================================================================
// Customizable dashboard (Phase 1a/1b). The dashboard fetches its per-tenant
// config (chosen metric keys), then the computed metrics for those keys; the
// Customize modal reads the catalog (pickable metadata) and PUTs the selection.
//   getConfig  → { selectedKeys:[...], isDefault, updatedAt }
//   getCatalog → { metrics:[{key,label,category,unit?,description?}] }
//   getMetrics → { metrics:[{key,label,value,unit?,category,meta?}], meta:{unknown,gated} }
//   putConfig  → { selectedKeys, isDefault, updatedAt }   (admin-gated: 403 otherwise)
// ============================================================================
export const dashboard = {
  // One request for the whole screen: { config, metrics, charts } off a single
  // shared data load. Replaces the old config → (metrics + charts) sequence.
  getSummary: (keys) => apiGet("/dashboard/summary" + qs({ keys: Array.isArray(keys) ? keys.join(",") : keys })),
  getConfig: () => apiGet("/dashboard/config"),
  getCatalog: () => apiGet("/dashboard/catalog"),
  getMetrics: (keys) => apiGet("/dashboard/metrics" + qs({ keys: Array.isArray(keys) ? keys.join(",") : keys })),
  getCharts: (keys) => apiGet("/dashboard/charts" + qs({ keys: Array.isArray(keys) ? keys.join(",") : keys })),
  putConfig: (selectedKeys) => apiPut("/dashboard/config", { selectedKeys }),
};

// ============================================================================
// Tasks (Phase 1a/1b backend). Standalone work items + recurring templates.
//   listTasks → { tasks:[{id,title,description,kind,status,dueDate,parentTaskId,
//                 assignee:{id,name}, createdBy:{id,name}, completedAt,
//                 completedBy:{id,name}|null, attachmentCount, createdAt}], total }
//     filters: assigneeId, createdByUserId, status, kind, parentTaskId, from, to,
//     includeTemplates (recurring TEMPLATES are excluded unless true / kind=recurring).
//   getTask → { task:{...full fields..., frequency,endDate,dayOfMonth,intervalDays,
//                 completionRemarks, attachments:{assignment:[...],completion:[...]}} }
//   createTask body: {title, description?, assigneeId, kind:'one_time'|'recurring',
//     dueDate?, and for recurring: frequency, endDate, dayOfMonth?/intervalDays?,
//     attachments?:[{kind:'link'|'file', linkUrl?/fileUrl?, label?}]}
//   setTaskStatus body: {status:'in_progress'|'completed'|'reopened'|'closed',
//     remarks?, attachments?:[...]}   (all optional on completion)
// ============================================================================
export const tasks = {
  listTasks: (opts = {}) =>
    apiGet(
      "/tasks" +
        qs({
          assigneeId: opts.assigneeId,
          createdByUserId: opts.createdByUserId,
          status: opts.status,
          kind: opts.kind,
          parentTaskId: opts.parentTaskId,
          from: opts.from,
          to: opts.to,
          q: opts.q,
          includeTemplates: opts.includeTemplates,
          limit: opts.limit,
          offset: opts.offset,
        }),
    ),
  // Active company users (id+name) for the assign-to picker — auth-only, so any
  // user can assign (GET /users is admin-gated and 403s for regular employees).
  listAssignees: () => apiGet("/tasks/assignees"),
  getTask: (id) => apiGet(`/tasks/${encodeURIComponent(id)}`),
  createTask: (body) => apiPost("/tasks", body),
  updateTask: (id, body) => apiPatch(`/tasks/${encodeURIComponent(id)}`, body),
  setTaskStatus: (id, body) => apiPatch(`/tasks/${encodeURIComponent(id)}/status`, body),
  deleteTask: (id) => apiDelete(`/tasks/${encodeURIComponent(id)}`),
  // System Controller bulk sweep (monitor/owner-gated in the backend).
  bulkDeleteTasks: (ids) => apiPost("/tasks/bulk-delete", { ids }),
};

// In-app notifications (P3.1b bell). Backend P3.1a: audience-scoped per user; retail-only emits
// (manufacturing tenants get { unreadCount: 0, items: [] } — an inert bell).
//   list()        → { unreadCount, items:[{id,type,title,body,entityRef,createdAt,read}] } (newest first)
//   markRead(ids?) → { ok, marked }  (ids omitted ⇒ mark ALL currently-visible read)
export const notifications = {
  list: () => apiGet("/notifications"),
  markRead: (ids) => apiPost("/notifications/read", ids && ids.length ? { ids } : {}),
};
